/**
 * cta-destino.js — DESTINO GLOBAL de los botones (CTA) del sitio.
 *
 * POR QUÉ EXISTE: `cta_url` NO está en el schema del LLM (core_services/llm_schemas.py), así que
 * el modelo nunca lo genera: TODOS los botones de una web recién creada nacen sin enlace y había
 * que ponerlos a mano, uno a uno, bloque a bloque y página a página. Este módulo permite fijar UN
 * destino a NIVEL DE SITIO (WhatsApp / teléfono / email / URL) que actúa como FALLBACK de todos
 * los CTA que no tengan enlace propio.
 *
 * MÓDULO PURO: no toca DOM ni `window` (ejercitable en node — ver tests/_cta_destino.mjs). Escrito
 * con sintaxis ES2019 (sin `?.` ni `??`) porque el harness de tests corre el FUENTE en node 12.
 *
 * EL DATO (nivel sitio, dentro de `datos_json_builder`, sin migración: es un JSONField):
 *   siteData.cta_destino = {
 *     tipo: '' | 'reserva' | 'whatsapp' | 'tel' | 'email' | 'url',  // '' = desactivado (previo)
 *     valor: '<página/ancla del sitio | número | teléfono | email | url>',
 *     mensaje: { es: '…', en: '…' },                     // SOLO whatsapp · POR IDIOMA
 *     traducir_mensaje: true,                            // interruptor (lo lee viewer.js al traducir)
 *     mostrar_flotante: true,                            // SOLO whatsapp · icono flotante sí/no
 *   }
 * Campo OPCIONAL con default vacío → los sitios que no lo tienen se comportan EXACTAMENTE como
 * antes, así que no toca CURRENT_SCHEMA_VERSION (schema_migrations.py).
 *
 * CÓMO SE APLICA (y por qué NO se hornea en los props):
 * `aplicarDestinoDeSitio()` deja la URL resuelta en `links.js` (estado de render, mismo patrón que
 * `setRenderLang` de i18n.js) y `ctaAnchorAttrs` la usa SOLO para el `href`, NUNCA para el
 * `data-link`. Esto es deliberado: `core/extractor.js` (línea ~165) persiste `data-link` como
 * `<campo>_url` de CADA bloque, y el autoguardado (viewer.js) extrae del DOM aunque estés en
 * "Ver Sitio" → si el fallback viajara en `data-link` quedaría HORNEADO como enlace propio de cada
 * botón y cambiar el destino global ya no actualizaría nada. Por eso NO basta con aplicarlo sobre
 * una copia de los props: hay que mantenerlo fuera de `data-link`.
 *
 * CONVIVENCIA: no duplica la infra WhatsApp existente (el número del footer, el botón flotante de
 * animations/whatsapp.js ni el `recipient_type:'whatsapp'` de los formularios). Este módulo
 * gobierna SOLO el destino de los BOTONES. Tampoco tiene que ver con `t('wa_prefill')` (i18n del
 * chrome, el mensaje por defecto del botón del footer): aquel es chrome determinista; este es un
 * texto del cliente que SÍ se traduce con la web.
 */
import { hrefSeguro, setCtaDestinoGlobal } from './links.js';

/**
 * Tipos de destino soportados. '' (o cualquier otro valor) = desactivado.
 *
 * `reserva` va PRIMERO porque en los negocios que reservan (restaurante, clínica, taller) "reservar"
 * es EL verbo del sitio: es el destino que más veces se elige, y con él los botones apuntan a la
 * propia página/sección de reservas en vez de sacar al visitante a otra app.
 */
export const TIPOS_DESTINO = ['reserva', 'whatsapp', 'tel', 'email', 'url'];

/** Tope del mensaje de WhatsApp: va dentro de la URL (?text=…), no es un campo de contenido. */
export const MENSAJE_MAX = 500;

// Icono de cada tipo en los desplegables que lo eligen (panel «Enlace» y Ajustes del sitio; lámina de los
// desplegables, 19-10-2026). Nombres de `utils/icons-ph.js`: SVG, nunca emojis.
export const ICONO_DESTINO = {
    reserva: 'link-simple', whatsapp: 'whatsapp-logo', tel: 'phone', email: 'envelope-simple', url: 'globe-simple',
};

// Mínimo de dígitos para dar por bueno un número. WhatsApp SIEMPRE lleva prefijo de país (de ahí el
// 7); en teléfono normal se baja a 6 porque hay fijos y números cortos de atención más breves.
const MIN_DIGITOS_WA = 7;
const MIN_DIGITOS_TEL = 6;

const _EMAIL_OK = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const _CON_ESQUEMA = /^[a-z][\w+.-]*:/i;
// Caracteres que NO pueden ir en un id de página al meterlo en `?page=<id>`: romperían la query
// (`&`, `=`, `#`, `?`) o el atributo del render (`<`, `>`, comillas, backtick, barra invertida).
const _SLUG_MALO = /[<>"'`\\?#&=]/;

function _txt(v) {
    return typeof v === 'string' ? v.trim() : '';
}

/**
 * Destino INTERNO (tipo 'reserva'): la página o la sección de reserva del PROPIO sitio. Acepta las
 * tres formas que el cliente puede tener a mano, y las deja en una URL interna:
 *   'reservas'          → '?page=reservas'   (id de página; misma convención que la nav del header)
 *   '?page=reservas'    → tal cual
 *   '#bloque-reservas'  → tal cual           (ancla de sección: render.js pinta id="bloque-<tipo>")
 *   '/reservar'         → tal cual           (ruta del propio dominio)
 *
 * NO admite esquema (`https:`, y por tanto tampoco `javascript:`/`data:`) ni `//host`: un enlace que
 * SALE del sitio es el tipo 'url'. Así lo peligroso muere aquí, sin depender solo de `hrefSeguro`.
 * Devuelve `null` si no se puede formar un destino interno → el panel avisa y el botón se queda como
 * está (href '#'), que es justo lo que exige el contrato de `construirUrl`.
 */
function _urlInterna(valor) {
    if (_CON_ESQUEMA.test(valor)) return null;
    if (valor.indexOf('//') === 0) return null;         // protocol-relative: se iría fuera del sitio
    const c = valor.charAt(0);
    if (c === '?' || c === '#' || c === '/') return valor;
    const slug = valor.replace(/\s+/g, '-');            // "mis reservas" → "mis-reservas"
    return _SLUG_MALO.test(slug) ? null : '?page=' + slug;
}

/**
 * Mensaje del destino para `lang`, con herencia del idioma base (misma regla que el resto del
 * multilingüe: si un idioma no tiene traducción propia, se usa la del idioma base para que el
 * botón nunca salga sin mensaje).
 */
export function mensajeDeIdioma(destino, lang, baseLang) {
    if (!destino || typeof destino !== 'object') return '';
    const m = destino.mensaje;
    if (!m || typeof m !== 'object') return '';
    const propio = _txt(m[String(lang || '').toLowerCase()]);
    if (propio) return propio;
    return _txt(m[String(baseLang || 'es').toLowerCase()]);
}

/**
 * URL final del destino global para el idioma `lang`, o `null` si no hay destino usable.
 *
 * El mensaje de WhatsApp se codifica UNA sola vez (encodeURIComponent): se guarda SIEMPRE en texto
 * plano; si se guardara ya codificado saldría `%2520`.
 * Valida SIEMPRE con `hrefSeguro` (allowlist de esquemas de links.js) → `javascript:` / `data:` y
 * cualquier cosa rara devuelven `null` y el botón se queda como está hoy (href="#").
 *
 * `reserva` es el único tipo INTERNO (ver `_urlInterna`): no se le añade sufijo de idioma a la
 * página destino, porque desde aquí no se sabe qué páginas espejo `-xx` existen de verdad (eso vive
 * en el store, y este módulo es puro); apuntar a una página inexistente sería peor que apuntar a la
 * del idioma base.
 */
export function construirUrl(destino, lang, baseLang) {
    if (!destino || typeof destino !== 'object') return null;
    const tipo = _txt(destino.tipo).toLowerCase();
    const valor = _txt(destino.valor);
    if (TIPOS_DESTINO.indexOf(tipo) === -1 || !valor) return null;

    let url = '';
    if (tipo === 'reserva') {
        // Destino interno: la página/sección de reserva del propio sitio. Sin `_urlInterna` válida
        // no hay destino (mismo criterio que un número corto en WhatsApp: mejor sin enlace que con
        // uno muerto).
        url = _urlInterna(valor);
        if (!url) return null;
    } else if (tipo === 'whatsapp') {
        // wa.me exige el número en formato internacional y SOLO dígitos (sin '+', espacios ni guiones).
        const num = valor.replace(/\D+/g, '');
        // Suelo de dígitos: al quedarnos solo con los números, cualquier texto pegado por error
        // ("llámanos al 1", una URL...) se convertiría en un wa.me/<basura> que parece un enlace
        // bueno y no lleva a ninguna parte. Un número de WhatsApp lleva prefijo de país: nunca es
        // tan corto. Devolver null hace que el panel avise en vez de guardar un botón muerto.
        if (num.length < MIN_DIGITOS_WA) return null;
        const msg = mensajeDeIdioma(destino, lang, baseLang).slice(0, MENSAJE_MAX);
        url = 'https://wa.me/' + num + (msg ? '?text=' + encodeURIComponent(msg) : '');
    } else if (tipo === 'tel') {
        const num = valor.replace(/[^\d+]/g, '');
        if (num.replace(/\D/g, '').length < MIN_DIGITOS_TEL) return null;   // mismo motivo que arriba
        url = 'tel:' + num;
    } else if (tipo === 'email') {
        if (!_EMAIL_OK.test(valor)) return null;
        url = 'mailto:' + valor;
    } else {
        // 'url': el cliente escribe "www.ejemplo.com" tanto como "https://…". Sin esquema (y sin ser
        // ruta/ancla) se asume https → si no, hrefSeguro lo cortaría y el botón quedaría muerto.
        const esRuta = valor.charAt(0) === '/' || valor.charAt(0) === '#' || valor.charAt(0) === '?';
        url = (_CON_ESQUEMA.test(valor) || esRuta) ? valor : 'https://' + valor;
    }
    // hrefSeguro devuelve '#' para todo lo que no pase la allowlist (y escapa para el atributo;
    // aquí solo se usa como VALIDADOR: se devuelve la URL en crudo, el escape lo hace el render).
    return hrefSeguro(url) === '#' ? null : url;
}

/**
 * Estado de render del BOTÓN FLOTANTE de WhatsApp (animations/whatsapp.js). Vive aquí, y no en los
 * props del footer, por la misma razón que la URL del CTA: si se escribiera en `footer.props` el
 * autoguardado lo persistiría y el número quedaría duplicado en dos sitios (destino global + footer)
 * pudiendo divergir. Se recalcula en cada pintado, como `setRenderLang` de i18n.js.
 *   { num, msg, oculto }  ·  `oculto` = el cliente apagó el flotante teniendo destino WhatsApp.
 */
let _waFlotante = { num: '', msg: '', oculto: false };

/**
 * Número y mensaje del flotante para el idioma que se está pintando. Lo consume `blocks/footer.js`,
 * que es quien emite los `data-wa-num`/`data-wa-msg` de los que se alimenta el botón flotante (así
 * el publicado lo hereda sin plumbing nuevo: el HTML ya sale con los atributos puestos).
 */
export function getWaFlotante() {
    return _waFlotante;
}

/**
 * Deja el destino global resuelto para el idioma que se va a pintar. Lo llaman las DOS superficies
 * de render (core/render.js para editor/preview y compileSiteHtml en pages/viewer.js para el HTML
 * publicado) → la paridad editor↔publicado queda garantizada por construcción (invariante nº12).
 * @returns {string} la URL aplicada ('' si no hay destino).
 */
export function aplicarDestinoDeSitio(destino, lang, baseLang) {
    const url = construirUrl(destino, lang, baseLang) || '';
    setCtaDestinoGlobal(url);

    // El flotante y los botones comparten número: UNA sola fuente de verdad. Con destino WhatsApp
    // el global manda sobre `footer.props.whatsapp` (que queda como respaldo de las webs anteriores
    // a este módulo); con `mostrar_flotante:false` se marca oculto para que el footer no emita los
    // atributos y el icono no aparezca — hay quien tiene bastante con los botones.
    const esWa = !!destino && _txt(destino.tipo).toLowerCase() === 'whatsapp';
    if (esWa && url) {
        _waFlotante = {
            num: _txt(destino.valor).replace(/\D+/g, ''),
            msg: mensajeDeIdioma(destino, lang, baseLang).slice(0, MENSAJE_MAX),
            oculto: destino.mostrar_flotante === false,
        };
    } else {
        _waFlotante = { num: '', msg: '', oculto: false };
    }
    return url;
}

/**
 * Normaliza lo que teclea el cliente (creador y editor comparten esta función) al objeto canónico,
 * o `null` si no hay destino (tipo vacío / sin valor) → el sitio se comporta como hoy.
 * @param {object} raw  { tipo, valor, mensaje (string del idioma base | mapa por idioma), traducir_mensaje }
 * @param {string} baseLang  idioma base del sitio (`siteData.idioma`).
 */
export function normalizarDestino(raw, baseLang) {
    if (!raw || typeof raw !== 'object') return null;
    const tipo = _txt(raw.tipo).toLowerCase();
    const valor = _txt(raw.valor).slice(0, 300);
    if (TIPOS_DESTINO.indexOf(tipo) === -1 || !valor) return null;

    const base = String(baseLang || 'es').toLowerCase();
    const mensaje = {};
    if (tipo === 'whatsapp') {
        if (typeof raw.mensaje === 'string') {
            const m = _txt(raw.mensaje).slice(0, MENSAJE_MAX);
            if (m) mensaje[base] = m;
        } else if (raw.mensaje && typeof raw.mensaje === 'object') {
            Object.keys(raw.mensaje).forEach(k => {
                const m = _txt(raw.mensaje[k]).slice(0, MENSAJE_MAX);
                if (m) mensaje[String(k).toLowerCase()] = m;
            });
        }
    }
    return {
        tipo: tipo,
        valor: valor,
        mensaje: mensaje,
        // Los dos interruptores solo se apagan explícitamente: por defecto el mensaje SÍ se traduce
        // y el botón flotante de WhatsApp SÍ se muestra (las webs anteriores no traen la clave).
        traducir_mensaje: raw.traducir_mensaje !== false,
        mostrar_flotante: raw.mostrar_flotante !== false,
    };
}

/**
 * Mapa de mensajes tras editar el texto del idioma BASE. Si el texto cambió se DESCARTAN las
 * traducciones anteriores: ya no corresponden a lo que dice el mensaje, y dejarlas vivas
 * escribiría en el botón inglés una traducción de un texto que el cliente acaba de reescribir
 * (silencioso y difícil de detectar). Al volver a traducir la web se regeneran.
 */
export function fijarMensajeBase(mensajeActual, texto, baseLang) {
    const base = String(baseLang || 'es').toLowerCase();
    const prev = (mensajeActual && typeof mensajeActual === 'object') ? mensajeActual : {};
    const nuevo = _txt(texto).slice(0, MENSAJE_MAX);
    if (_txt(prev[base]) === nuevo) return prev;
    const out = {};
    if (nuevo) out[base] = nuevo;
    return out;
}
